import map from 'lodash/map';
import each from 'lodash/each';
import find from 'lodash/find';
import omitBy from 'lodash/omitBy';
import without from 'lodash/without';
import compact from 'lodash/compact';
import slice from 'lodash/slice';
import isEmpty from 'lodash/isEmpty';

import { generateEid } from 'zoomforth_codesbane_editor/lib/id_generators.js';


// Apply the available overrides to each content item in the section
export const applyOrderedContentSectionOverrides = (orderedContentUuids, sectionOverrides)=> {

  // If a new section is just created then it will likely render before the
  // overrides have been set...which is bad.
  if (!sectionOverrides) {
    return map(orderedContentUuids, contentUuid => ({ uuid: contentUuid }));
  }

  const { contentEids, contentOverrideDataByEid } = sectionOverrides;

  // make a copy since we edit shift these things out
  const contentUuids = [ ...orderedContentUuids ];

  return map(contentEids, eid => {
    const overrideData = { eid, uuid: contentUuids.shift() };

    if (contentOverrideDataByEid[eid]) {
      return { ...overrideData, ...contentOverrideDataByEid[eid] };
    }
    return overrideData;
  });
};


// Add a new loading piece of content to the override data
export const createNewLoadingContent = (section, sectionOverrides, { positionData } = {}) => {
  const { contentEids, contentUuidsByEid, contentOverrideDataByEid } = sectionOverrides;

  const newEid = generateEid();
  var newEids = contentEids;

  if (!isEmpty(positionData)) {
    const { atIndex } = positionData;

    newEids = [
      ...slice(newEids, 0, atIndex),
      newEid,
      ...slice(newEids, atIndex)
    ];

  } else {
    newEids.push(newEid);
  }

  contentUuidsByEid[newEid] = null;
  contentOverrideDataByEid[newEid] = {
    isLoading: true,
    isInIntermediateState: true
  };

  const newSection = {
    ...section,
    orderedContentUuids: 
      compact(map(applyOrderedContentSectionOverrides(section.orderedContentUuids, sectionOverrides), 'uuid'))
  };

  const newSectionOverrides = {
    contentEids: newEids,
    contentUuidsByEid,
    contentOverrideDataByEid
  };

  return [
    newEid,
    newSection,
    newSectionOverrides
  ];
};


// Update overridden data for a given EID
export const updateContentDataForEid = (section, sectionOverrides, eidToUpdate, newData) => {
  const { contentEids, contentUuidsByEid, contentOverrideDataByEid } = sectionOverrides;

  const newSectionOverrides = {
    contentEids,
    contentUuidsByEid: {
      ...contentUuidsByEid,
      [eidToUpdate]: getContentUuidByEid(section, sectionOverrides, eidToUpdate)
    },
    contentOverrideDataByEid: {
      ...contentOverrideDataByEid,
      [eidToUpdate]: newData
    }
  };

  return [
    section,
    newSectionOverrides
  ];
};


// Remove overridden data for a given EID
export const removeContentDataForEid = (section, sectionOverrides, eidToRemove) => {
  const { contentUuidsByEid, contentOverrideDataByEid } = sectionOverrides;

  const newSectionOverrides = {
    ...sectionOverrides,
    contentUuidsByEid: omitBy(
      contentUuidsByEid, (uuid, eid) => eidToRemove === eid
    ),
    contentOverrideDataByEid: omitBy(
      contentOverrideDataByEid, (data, eid) => eidToRemove === eid
    )
  };

  return [
    section,
    newSectionOverrides
  ];
};


// Remove a content item from the overrides including its EID
export const removeContentItemForEid = (section, sectionOverrides, eidToRemove) => {
  const { contentEids, contentUuidsByEid, contentOverrideDataByEid } = sectionOverrides;

  const uuidToRemove = contentUuidsByEid[eidToRemove];
  const newSection = {
    ...section,
    orderedContentUuids: without(section.orderedContentUuids, uuidToRemove)
  };

  const newSectionOverrides = {
    contentEids: without(contentEids, eidToRemove),
    contentUuidsByEid: omitBy(
      contentUuidsByEid, (uuid, eid) => eidToRemove === eid
    ),
    contentOverrideDataByEid: omitBy(
      contentOverrideDataByEid, (data, eid) => eidToRemove === eid
    )
  };

  return [
    newSection,
    newSectionOverrides
  ];
};


// Get the EID for a given contentUuid
export const getEidByContentUuid = (section, sectionOverrides, contentUuid)=> {
  const overriddenContent = applyOrderedContentSectionOverrides(section.orderedContentUuids, sectionOverrides);
  const { eid } = find(overriddenContent, ['uuid', contentUuid]);
  return eid;
};


// Get the contentUuid for a given EID
export const getContentUuidByEid = (section, sectionOverrides, eid)=> {
  const overriddenContent = applyOrderedContentSectionOverrides(section.orderedContentUuids, sectionOverrides);
  const { uuid } = find(overriddenContent, ['eid', eid]);
  return uuid;
};


// Build the initial section data
export const buildInitialSectionOverrides = (section)=> {
  const { orderedContentUuids } = section;

  const contentEids = [];
  const contentUuidsByEid = {};
  const contentOverrideDataByEid = {};

  each(orderedContentUuids, ()=> {
    let eid = generateEid();
    contentEids.push(eid);
  });

  const sectionOverrides = {
    contentEids,
    contentUuidsByEid,
    contentOverrideDataByEid
  };

  return sectionOverrides;
};
